import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, CheckCircle2, CircleDashed, ListChecks } from 'lucide-react';
import { Card } from '@components/base/layout';
import { Text, Badge, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Alert, Spinner } from '@components/base/feedback';
import {
  DataList,
  useDataList,
  dataListWorkflowRegistry,
} from '@components/integrated/data-list';
import type { DataListConfig } from '@components/integrated/data-list';
import type { ActionsConfig, CustomActionConfig } from '@components/integrated/entity-form';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromDataList,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import { taskService, type ClaimTaskRow } from '../api/taskService';
import { useClaimDataStore } from './claimDataStore';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ClaimTasksCardProps {
  /** Internal claim id — the card (re)fetches the claim's tasks from it. */
  idSin: string;
  /**
   * The task currently open on the claim screen (`ID_EVENT`, the `:taskId`
   * route param). Forwarded to VEOS as `id_event`; its row is highlighted and
   * clicking it is a no-op (the user is already on it). Optional: a task-less
   * claim screen omits it.
   */
  taskId?: string;
  /**
   * Veos session id, carried in the navigation state when a row opens another
   * task (same as the dashboard's task list), so the next screen boots with it.
   */
  ses?: string;
  /**
   * Per-tenant layout overrides injected by the `withComponentRules` HOC.
   * Underscored to match the injected meta-prop convention; `undefined` at
   * baseline ⇒ the config is returned unchanged (byte-identical render).
   * Rule authors target stable keys only:
   *
   * - `columns` — visible `statut` / `date` / `libelle` / `categorie` /
   *   `destinataire` / `action`, hidden by default `classementLib` /
   *   `libelleTypeTache` / `idEvent` (revealed with `showColumn`): rename /
   *   hide / show / reorder / tooltip;
   * - `actions` — the per-row `close` ("Clôturer") button: relabel / hide;
   * - `title` — the card title.
   *
   * The card keeps owning fetch, navigation and the close call, so an override
   * can never affect them.
   */
  _layoutOverrides?: LayoutOverrides;
}

/** Stable registry / layout-config id of this card. */
const COMPONENT_ID = 'claimflow.claimTasksCard';

/** Card-level texts (title / statuses / states), kept out of the JSX. */
const CLAIM_TASKS_CARD_TEXT = {
  title: 'Tâches',
  openCountTooltip: 'Tâches en cours',
  statusDone: 'Terminée',
  statusOpen: 'En cours',
  closeTooltip: 'Clôturer cette tâche',
  loadError: 'Erreur lors du chargement des tâches.',
  closeErrorTitle: 'La clôture a échoué',
  closeError: 'La clôture de la tâche a échoué.',
  empty: 'Aucune tâche',
} as const;

/** VEOS `STATUT` of a closed task (anything else is open, e.g. "EN COURS"). */
const STATUT_DONE = 'TERMINE';

function isTaskDone(task: ClaimTaskRow): boolean {
  return task.STATUT === STATUT_DONE;
}

function isTaskClosable(task: ClaimTaskRow): boolean {
  return task.ACTION_CLOTURE === '1';
}

// ---------------------------------------------------------------------------
// Row action config
//
// The per-row "Clôturer" button is a custom action keyed `close`, published
// under `actions` and merged with `_layoutOverrides.actions` (`setActionLabel`
// / `hideAction`) — same convention as the Documents card's "Ouvrir" button.
// The card wires its click itself; `handler` only names it. It is rendered by
// a custom cell renderer (not DataList `rowActions`, which render label-only
// buttons and ignore `icon`).
// ---------------------------------------------------------------------------

const CLOSE_ACTION_KEY = 'close';

const CLOSE_ACTION = {
  id: CLOSE_ACTION_KEY,
  handler: 'claimflow-claimTaskClose',
  label: 'Clôturer',
  variant: 'ghost',
  size: 'sm',
  // Outlined ghost button, as on the mockup.
  className: 'border border-secondary-200',
} satisfies CustomActionConfig;

const CLAIM_TASKS_ACTIONS: ActionsConfig = { custom: [CLOSE_ACTION] };

/** The close button as resolved for this render (overrides applied). */
interface ResolvedCloseAction {
  label: string;
  visible: boolean;
}

// ---------------------------------------------------------------------------
// Per-row render context
//
// The custom cell renderers + the row-click handler are module singletons
// (registered once below), but the busy state, the current-task flag and the
// navigation / close callbacks belong to the card instance. Each row carries
// them under `__ctx`, which the singletons read from `row` at render / click
// time. Rows are rebuilt whenever that state changes (see `rows` useMemo) so
// the closures stay fresh — no module-global mutable state, multi-instance
// safe (mirrors `DocumentsCard` / `MissingPiecesCard`).
// ---------------------------------------------------------------------------

interface ClaimTaskRowContext {
  /** This row is the task open on the screen. */
  current: boolean;
  /** Its close call is in flight. */
  closing: boolean;
  /** The close button is disabled (its close or a list refresh is in flight). */
  closeDisabled: boolean;
  closeAction: ResolvedCloseAction;
  /** Open the task's processing screen (no-op for the current task). */
  onOpen: () => void;
  /** Close the task. */
  onClose: () => void;
}

/**
 * Raw, comparable values the DataList sorts the custom-rendered columns on,
 * mapped per column key through `dataSource.fieldMapping` (`__sort.<key>`).
 */
interface ClaimTaskSortKeys {
  /** `0` for an open task, `1` for a closed one — ascending ⇒ open first. */
  statut: number;
}

interface ClaimTaskListRow extends ClaimTaskRow {
  /** Stable React key for the DataList table (it keys rows by `row.id`). */
  id: number;
  __sort: ClaimTaskSortKeys;
  /** Row-level marker read by `formatting.rowRules` (current-task highlight). */
  __current: boolean;
  __ctx: ClaimTaskRowContext;
}

function getRowContext(row: Record<string, unknown>): ClaimTaskRowContext | undefined {
  return (row as Partial<ClaimTaskListRow>).__ctx ?? undefined;
}

// ---------------------------------------------------------------------------
// Custom cell renderers + row-click handler (registered once at module load)
// ---------------------------------------------------------------------------

const STATUT_CELL_RENDERER = 'claimflow-claimTaskStatut';
const ACTION_CELL_RENDERER = 'claimflow-claimTaskClose';
const ROW_CLICK_HANDLER = 'claimflow-claimTaskOpen';

// "Statut" column: icon only — a green check for a closed task, a red dashed
// circle for an open one; the status label is the icon's accessible name and
// hover title. `aria-current` flags the task open on the screen (the DataList
// `<tr>` exposes no aria hook — same as the Documents card's active row).
dataListWorkflowRegistry.registerCellRenderer(STATUT_CELL_RENDERER, (_value, row) => {
  const task = row as unknown as ClaimTaskRow;
  const done = isTaskDone(task);
  const label = done ? CLAIM_TASKS_CARD_TEXT.statusDone : CLAIM_TASKS_CARD_TEXT.statusOpen;
  const current = getRowContext(row)?.current ?? false;
  return (
    <span className="inline-flex" title={label} aria-current={current ? 'true' : undefined}>
      <Icon
        icon={done ? CheckCircle2 : CircleDashed}
        size="sm"
        color={done ? 'success' : 'error'}
        aria-label={label}
      />
    </span>
  );
});

// "Clôturer" column: a ghost button with a check icon, only on the rows VEOS
// flags as closable (`ACTION_CLOTURE === "1"`). The click never reaches the
// row (`stopPropagation`), so it does not open the task. While the close call
// runs the button shows its spinner and is disabled.
dataListWorkflowRegistry.registerCellRenderer(ACTION_CELL_RENDERER, (_value, row) => {
  const task = row as unknown as ClaimTaskRow;
  const ctx = getRowContext(row);
  if (!ctx || !isTaskClosable(task) || !ctx.closeAction.visible) return null;
  return (
    <Button
      variant={CLOSE_ACTION.variant}
      size={CLOSE_ACTION.size}
      className={CLOSE_ACTION.className}
      leftIcon={<Icon icon={Check} size="sm" />}
      loading={ctx.closing}
      disabled={ctx.closeDisabled}
      title={CLAIM_TASKS_CARD_TEXT.closeTooltip}
      onClick={(event) => {
        event.stopPropagation();
        ctx.onClose();
      }}
    >
      {ctx.closeAction.label}
    </Button>
  );
});

dataListWorkflowRegistry.registerRowClickHandler(ROW_CLICK_HANDLER, (row) => {
  getRowContext(row)?.onOpen();
});

// ---------------------------------------------------------------------------
// Config
//
// Declarative DataListConfig with stable column keys; French labels live only
// in `column.label` (display-only / overridable). `date` is a native `date`
// column on the raw epoch `DATE_DEBUT` (displayed `dd/MM/yyyy`, sorted
// chronologically); `statut` sorts on `__sort.statut` (open first ascending).
// The list keeps the API order (open tasks first) until the user clicks a
// header (none → asc → desc). The current task's row is highlighted from the
// per-row `__current` marker via `formatting.rowRules`.
// ---------------------------------------------------------------------------

/** Display format of the start date (VEOS dates are epoch ms). */
const DATE_FORMAT = 'dd/MM/yyyy';

function createClaimTasksListConfig(rows: ClaimTaskListRow[]): DataListConfig {
  return {
    id: COMPONENT_ID,

    dataSource: {
      data: rows as unknown as Record<string, unknown>[],
      // Column key → what the column displays (text / date columns) or sorts
      // on (custom columns, which render from the row itself).
      fieldMapping: {
        statut: '__sort.statut',
        date: 'DATE_DEBUT',
        libelle: 'LIBELLE',
        categorie: 'CATEGORIE',
        destinataire: 'NUM_PER',
        classementLib: 'CLASSEMENT_LIB',
        libelleTypeTache: 'LIBELLE_TYPE_TACHE',
        idEvent: 'ID_EVENT',
      },
    },

    columns: {
      // --- Visible by default (mockup order) -------------------------------
      statut: {
        label: 'Statut',
        order: 1,
        align: 'left',
        width: '80px',
        type: 'custom',
        typeOptions: { renderer: STATUT_CELL_RENDERER },
        sortable: true,
      },
      date: {
        label: 'Date',
        headerTooltip: 'Date de début de la tâche',
        order: 2,
        align: 'left',
        type: 'date',
        typeOptions: { format: DATE_FORMAT },
        sortable: true,
      },
      libelle: {
        label: 'Libellé',
        order: 3,
        align: 'left',
        type: 'text',
        className: 'font-semibold',
        sortable: true,
      },
      categorie: {
        label: 'Catégorie',
        order: 4,
        align: 'left',
        type: 'text',
        className: 'text-secondary-500',
        sortable: true,
      },
      destinataire: {
        label: 'Destinataire',
        headerTooltip: 'Destinataire de la tâche',
        order: 5,
        align: 'left',
        type: 'text',
        sortable: true,
      },
      action: {
        label: '',
        order: 6,
        align: 'right',
        type: 'custom',
        typeOptions: { renderer: ACTION_CELL_RENDERER },
        sortable: false,
      },

      // --- Hidden by default (an admin can reveal them with showColumn) ----
      classementLib: {
        label: 'Classement',
        order: 7,
        hidden: true,
        align: 'left',
        type: 'text',
        sortable: true,
      },
      libelleTypeTache: {
        label: 'Type de tâche',
        order: 8,
        hidden: true,
        align: 'left',
        type: 'text',
        sortable: true,
      },
      idEvent: {
        label: 'N° tâche',
        order: 9,
        hidden: true,
        align: 'left',
        type: 'text',
        sortable: true,
      },
    },

    display: {
      size: 'compact',
      hoverable: true,
    },

    // Front-only sorting on the loaded rows (no API call); API order until the
    // user clicks a header.
    sorting: {
      enabled: true,
    },

    toolbar: {
      visible: false,
    },

    actions: {
      rowClick: { handler: ROW_CLICK_HANDLER },
    },

    // Current-task highlight (left primary border + tinted background). Every
    // row gets a transparent left border via `cssOverrides.row` so the
    // highlight does not shift the layout (same as the Documents card).
    formatting: {
      rowRules: [
        {
          condition: { field: '__current', operator: 'equals', value: true },
          className: 'border-l-4 border-l-primary-600 bg-secondary-100',
        },
      ],
    },

    cssOverrides: {
      row: 'border-l-4 border-l-transparent',
    },

    // Every task on one page — no pager.
    pagination: {
      mode: 'scroll',
      pageSize: 999_999,
    },
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Identity of one fetch: the claim, the open task, and the tasks version. */
function buildRequestKey(idSin: string, taskId: string | undefined, version: number): string {
  return JSON.stringify([idSin, taskId ?? '', version]);
}

/** The tasks on screen, tagged with the request they answer. */
interface ClaimTasksLoadState {
  /** {@link buildRequestKey} of the request. */
  key: string;
  idSin: string;
  rows: ClaimTaskRow[];
  error: string | null;
}

const NO_CLOSING: ReadonlySet<string> = new Set<string>();

export function ClaimTasksCard({
  idSin,
  taskId,
  ses,
  _layoutOverrides,
}: Readonly<ClaimTasksCardProps>) {
  const navigate = useNavigate();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  // Bumped whenever a task of the claim is created or closed (Créer une tâche,
  // Clôturer la tâche, this card's own "Clôturer") — refetch on it.
  const tasksVersion = useClaimDataStore((s) => s.tasksVersion);
  const bumpTasks = useClaimDataStore((s) => s.bumpTasks);
  const markTaskClosed = useClaimDataStore((s) => s.markTaskClosed);

  const [loaded, setLoaded] = useState<ClaimTasksLoadState | null>(null);
  // `ID_EVENT`s whose close call is in flight (per-row busy state).
  const [closingIds, setClosingIds] = useState<ReadonlySet<string>>(NO_CLOSING);
  // Last close failure, tagged with its claim so it never lingers on another one.
  const [closeFailure, setCloseFailure] = useState<{ idSin: string; message: string } | null>(
    null,
  );

  // Stale-response guard: every request takes a new sequence number and only
  // the latest one issued may write state, so a slow response (another claim,
  // a task switch, a refresh overtaken by a newer one) never overwrites a
  // newer result.
  const requestRef = useRef(0);

  useEffect(() => {
    // Bumped even without a claim id, so an in-flight response is dropped.
    const requestId = ++requestRef.current;
    if (idSin === '') return;
    const key = buildRequestKey(idSin, taskId, tasksVersion);
    taskService.fetchClaimTasks(idSin, taskId).then(
      (rows) => {
        if (requestId !== requestRef.current) return;
        setLoaded({ key, idSin, rows, error: null });
      },
      () => {
        if (requestId !== requestRef.current) return;
        setLoaded({ key, idSin, rows: [], error: CLAIM_TASKS_CARD_TEXT.loadError });
      },
    );
  }, [idSin, taskId, tasksVersion]);

  // Only the CURRENT claim's tasks are ever shown: full spinner until its
  // first response settles (first load and after a claim switch). A refetch of
  // the same claim (task switch, task created / closed) keeps the rows on
  // screen with a discreet header spinner. Without a claim id nothing is
  // fetched and the card reads as empty.
  const hasClaim = idSin !== '';
  const current = hasClaim && loaded?.idSin === idSin ? loaded : null;
  const isInitialLoading = hasClaim && current === null;
  const isRefetching =
    current !== null && current.key !== buildRequestKey(idSin, taskId, tasksVersion);
  const tasks = useMemo(() => current?.rows ?? [], [current]);
  const loadError = current?.error ?? null;
  const closeError = closeFailure?.idSin === idSin ? closeFailure.message : null;
  const openCount = useMemo(() => tasks.filter((task) => !isTaskDone(task)).length, [tasks]);

  // Row click: open the task's processing screen — the same navigation as the
  // dashboard's task list (`TaskListCard`).
  const openTask = useCallback(
    (task: ClaimTaskRow) => {
      if (!tenantSlug) return;
      const taskIdToOpen = String(task.ID_EVENT);
      const taskIdSin = String(task.ID_SIN);
      void navigate(`/claimflow/${tenantSlug}/task/${taskIdToOpen}?id_sin=${taskIdSin}`, {
        state: { ses, id_sin: taskIdSin },
      });
    },
    [navigate, tenantSlug, ses],
  );

  // "Clôturer": the shared close-task call (the one "Clôturer la tâche" uses),
  // with the row's task id; no confirmation (spec). On success the task is
  // first signalled as closed — the "Clôturer la tâche" card of that task
  // switches to its closed state — then the tasks version is bumped, which
  // refetches this list (and any other subscriber).
  const closeTask = useCallback(
    async (idEvent: string) => {
      setCloseFailure(null);
      setClosingIds((prev) => new Set(prev).add(idEvent));
      try {
        await taskService.closeTask(idEvent);
        markTaskClosed(idEvent);
        bumpTasks();
      } catch (error) {
        setCloseFailure({
          idSin,
          message:
            error instanceof Error && error.message !== ''
              ? error.message
              : CLAIM_TASKS_CARD_TEXT.closeError,
        });
      } finally {
        setClosingIds((prev) => {
          const next = new Set(prev);
          next.delete(idEvent);
          return next;
        });
      }
    },
    [idSin, markTaskClosed, bumpTasks],
  );

  // The "Clôturer" button resolved against the tenant overrides (identity at
  // baseline).
  const closeOverride = _layoutOverrides?.actions?.[CLOSE_ACTION_KEY];
  const closeLabel = closeOverride?.label ?? CLOSE_ACTION.label;
  const closeVisible = closeOverride?.visible ?? true;
  const closeAction = useMemo<ResolvedCloseAction>(
    () => ({ label: closeLabel, visible: closeVisible }),
    [closeLabel, closeVisible],
  );

  // Per-render rows: the data + the live per-row context. Rebuilt whenever the
  // data, the current task, the busy state or the callbacks change so the
  // renderers' / row-click closures stay fresh.
  const rows = useMemo<ClaimTaskListRow[]>(
    () =>
      tasks.map((task) => {
        const idEvent = String(task.ID_EVENT);
        const isCurrent = taskId !== undefined && idEvent === taskId;
        const closing = closingIds.has(idEvent);
        return {
          ...task,
          id: task.ID_EVENT,
          __sort: { statut: isTaskDone(task) ? 1 : 0 },
          __current: isCurrent,
          __ctx: {
            current: isCurrent,
            closing,
            closeDisabled: closing || isRefetching,
            closeAction,
            onOpen: () => {
              if (!isCurrent) openTask(task);
            },
            onClose: () => {
              void closeTask(idEvent);
            },
          },
        };
      }),
    [tasks, taskId, closingIds, isRefetching, closeAction, openTask, closeTask],
  );

  const baseConfig = useMemo(() => createClaimTasksListConfig(rows), [rows]);

  // Publish the columns AND the row action so the layout editor lists every
  // rule target of this card.
  usePublishLayoutConfig(COMPONENT_ID, {
    ...buildPublishedConfigFromDataList(baseConfig, CLAIM_TASKS_CARD_TEXT.title),
    actions: CLAIM_TASKS_ACTIONS,
  });

  const config = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );

  const list = useDataList(config);

  return (
    <Card padding="none">
      <Card.Header>
        <div className="flex items-center gap-2">
          <Icon icon={ListChecks} size="md" color="primary" />
          <Text as="span" weight="semibold" size="base">
            {resolveCardTitle(CLAIM_TASKS_CARD_TEXT.title, _layoutOverrides)}
          </Text>
          {!isInitialLoading && !loadError && (
            <Badge
              variant="default"
              size="sm"
              pill
              title={CLAIM_TASKS_CARD_TEXT.openCountTooltip}
            >
              {openCount}
            </Badge>
          )}
          {/* A refetch keeps the rows on screen, with this discreet hint. */}
          {isRefetching && <Spinner size="xs" />}
        </div>
      </Card.Header>
      <Card.Content>
        {isInitialLoading && (
          <div className="flex items-center justify-center min-h-24 p-5">
            <Spinner size="md" />
          </div>
        )}

        {!isInitialLoading && loadError && (
          <div className="p-5">
            <Alert variant="error">{loadError}</Alert>
          </div>
        )}

        {!isInitialLoading && !loadError && closeError && (
          <div className="px-5 pt-5">
            <Alert
              variant="error"
              title={CLAIM_TASKS_CARD_TEXT.closeErrorTitle}
              dismissible
              onDismiss={() => {
                setCloseFailure(null);
              }}
            >
              {closeError}
            </Alert>
          </div>
        )}

        {!isInitialLoading && !loadError && tasks.length === 0 && (
          <div className="p-5">
            <Text size="sm" color="muted">
              {CLAIM_TASKS_CARD_TEXT.empty}
            </Text>
          </div>
        )}

        {!isInitialLoading && !loadError && tasks.length > 0 && (
          <div data-testid="claim-tasks-list" aria-busy={isRefetching}>
            <DataList config={config} list={list}>
              <DataList.Table />
            </DataList>
          </div>
        )}
      </Card.Content>
    </Card>
  );
}
